<template>
  <div class="additional-info-tab">
    <!-- Language removal is handled by the × on the language pill (LanguagePills),
         so this tab only keeps its Save action. -->
    <div class="info-tab-bar">
      <SegmentedTabs v-model="activeInfoSection" :tabs="infoSectionTabs" />
      <CustomButton v-if="!hideSave" variant="primary" :label="options.Lang.Speichern" @click="submitTabContent" />
    </div>

    <div v-show="activeInfoSection === 'general'" class="info-section">
      <div class="xi-card">
        <div v-if="label === 'de'" class="xi-switch">
          <CustomSwitch v-model="address.casual" />
          <span><md-icon class="xi-switch__icon">spellcheck</md-icon>{{ options.Lang.casual_switch }}</span>
        </div>

        <div class="xi-grid xi-grid--dense">
          <CustomInput :label="titleCase((options.Lang && options.Lang.BetreffSupplier) || '')" v-model="address.emailBetreffSupplier" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.Impressum) || '')" v-model="address.impressum" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.reiseversicherung) || '')" v-model="address.reiseversicherung" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.agb) || '')" v-model="address.agbs" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.AuswertigesAmt) || '')" v-model="address.amt" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.mitarbeiterbezeichnung) || '')" v-model="address.mitarbeiterbezeichnung" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.ReplyZugang) || '')" v-model="address.replyzugang" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.FooterInfo) || '')" v-model="address.vermittelt" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.Datenschutz) || '')" v-model="address.datenschutz" />
          <CustomInput :label="titleCase((options.Lang && options.Lang.travel_regulations) || '')" v-model="address.travel_regulations" />
        </div>
      </div>
    </div>

    <div v-show="activeInfoSection === 'emails'" class="info-section">
      <div class="xi-card">
        <!-- Variables reference accordion -->
        <div class="xi-vars">
          <button
              type="button"
              class="xi-vars__toggle"
              @click="showVarsOpen = !showVarsOpen"
              :aria-expanded="showVarsOpen.toString()"
              aria-controls="vars-table"
          >
            <md-icon>{{ showVarsOpen ? 'expand_less' : 'expand_more' }}</md-icon>
            <strong>* {{ options.Lang.vars }}</strong>
            <span class="xi-vars__hint">{{ options.Lang.variables.description || '(can be used inside editors that have the "Variable" sign)' }}</span>
          </button>
          <table v-show="showVarsOpen" id="vars-table" class="xi-vars__table">
            <thead>
              <tr>
                <th>* {{ options.Lang.vars }}</th>
                <th>{{ options.Lang.poiDescription }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(desc, key) in variableDescriptionObject" :key="key">
                <!-- span, not <code>: pasting <code> into an editor brings code formatting, which drops [link]'s link -->
                <td><span class="xi-vars__key">[{{ key }}]</span></td>
                <td>
                  {{ desc }}
                  <em v-if="reservationOnlyVars.includes(key)" class="xi-vars__scope">
                    ({{ options.Lang.variables.reservation_only || 'reservation email only' }})
                  </em>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Salutation choice: controls what [title] expands to for this
             branding + language. "Default" keeps the locale wording. -->
        <div class="xi-salutation">
          <div class="xi-field xi-salutation__field">
            <label class="xi-label">{{ titleCase(salutationT.label || 'Salutation') }}</label>
            <CustomSelect
                :value="salutationValue"
                :options="salutationOptions"
                :tick="true"
                :actionLabel="salutationT.add_choice || 'Add choice'"
                @input="onSalutationSelect"
                @action="openSalutationModal(null)"
                @edit="requestSalutationEdit"
                @remove="requestSalutationRemove" />
          </div>
          <span class="xi-salutation__hint">{{ salutationT.hint || 'Controls what [title] is replaced with in the texts.' }}</span>
        </div>

        <SalutationChoiceModal
            :show="showSalutationModal"
            :options="options"
            :langCode="label"
            :choice="salutationEditTarget"
            @save="saveSalutationChoice"
            @cancel="closeSalutationModal" />

        <DeleteConfirmation
            :showConfirm.sync="confirmSalutationDelete"
            :confirmTitle="salutationT.delete_title || 'Delete salutation'"
            :confirmMessage="salutationT.delete_text || 'Do you really want to delete this salutation?'"
            :trans="options.Lang"
            @confirm="removeSalutationChoice" />

        <div class="xi-grid">
          <div class="xi-field">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.BetreffCustomerLogin) || '') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.subject" /><editor-content class="xi-editor-content" :editor="editors?.subject" /></div>
            <VariableHint :options="options" editor-key="subject" />
          </div>
          <div class="xi-field">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.Signatur) || '') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.signature" /><editor-content class="xi-editor-content" :editor="editors?.signature" /></div>
            <VariableHint :options="options" editor-key="signature" />
          </div>
          <div class="xi-field">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.sayHello) || '') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.welcomeText" /><editor-content class="xi-editor-content" :editor="editors?.welcomeText" /></div>
            <VariableHint :options="options" editor-key="welcomeText" />
          </div>
          <div class="xi-field">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.sayHello_booked) || '') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.welcomeTextBooked" /><editor-content class="xi-editor-content" :editor="editors?.welcomeTextBooked" /></div>
            <VariableHint :options="options" editor-key="welcomeTextBooked" />
          </div>
          <div class="xi-field">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.infoDocsText) || '') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.infoDocs" /><editor-content class="xi-editor-content" :editor="editors?.infoDocs" /></div>
            <VariableHint :options="options" editor-key="infoDocs" />
          </div>
          <div class="xi-field">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.passolution.crm_title) || 'Travel Requirements Message') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.travelReqMsg" /><editor-content class="xi-editor-content" :editor="editors?.travelReqMsg" /></div>
            <VariableHint :options="options" editor-key="travelReqMsg" />
          </div>
          <div class="xi-field">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.reservation_email_body) || 'Reservation Email Body') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.reservationEmailBody" :image="true" /><editor-content class="xi-editor-content" :editor="editors?.reservationEmailBody" /></div>
            <VariableHint :options="options" editor-key="reservationEmailBody" />
          </div>
          <div class="xi-field">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.Emailvorlage_bearbeiten) || '') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.template" :image="true" /><editor-content class="xi-editor-content" :editor="editors?.template" /></div>
            <VariableHint :options="options" editor-key="template" />
          </div>
          <div class="xi-field" v-if="showSupplierMail">
            <label class="xi-label">{{ titleCase((options.Lang && options.Lang.SupplierSig) || '') }}</label>
            <div class="xi-editor"><TextEditorBox class="branding-tabs" :options="options" :editor="editors?.signatureSupplier" :image="true" /><editor-content class="xi-editor-content" :editor="editors?.signatureSupplier" /></div>
            <VariableHint :options="options" editor-key="signatureSupplier" />
          </div>
        </div>
      </div>
    </div>

    <div v-show="activeInfoSection === 'extra'" class="info-section">

      <!-- Intro: header title + content shown above the panels on the frontend. -->
      <div class="xi-intro">
        <CustomInput :label="titleCase(options.Lang.additional_information_tab.header_title || 'Title')" v-model="internal.extra_title" />
        <div class="xi-field">
          <label class="xi-label">{{ titleCase(options.Lang.additional_information_tab.header_content || 'Content') }}</label>
          <div class="xi-editor">
            <TextEditorBox class="branding-tabs" :options="options" :editor="editors?.extra_content" :image="true" />
            <editor-content class="xi-editor-content" :editor="editors?.extra_content" />
          </div>
        </div>
        <p class="xi-explanation"><i>{{ options.Lang.additional_information_tab.explanation }}</i></p>
      </div>

      <!-- Collapsible info panels (cards). -->
      <div class="xi-panels">
        <div v-for="(panel, pIndex) in extraInformationPanels" :key="panel.uid" class="xi-panel">
          <DeleteConfirmation
              :showConfirm.sync="panel._confirmDelete"
              :confirmTitle="options.Lang?.additional_information_tab.delete_title || 'Delete Item'"
              :confirmMessage="options.Lang?.additional_information_tab.delete_text || 'Do you really want to delete this item?'"
              :trans="options.Lang"
              @confirm="removeExtraInformationPanel(pIndex, panel)"
          />
          <div class="xi-panel__head">
            <span class="xi-panel__num">{{ pIndex + 1 }}</span>
            <input class="xi-input xi-panel__title" type="text" v-model="panel.title"
                   :placeholder="options.Lang.additional_information_tab.boxes_title || 'Title'" />
            <CustomButton class="xi-panel__remove" variant="ghost" icon="delete" :iconOnly="true"
                          :title="options.Lang?.löschen || 'Remove'" @click="panel._confirmDelete = true" />
          </div>

          <div class="xi-field">
            <label class="xi-label">{{ titleCase(options.Lang.additional_information_tab.boxes_content || 'Content') }}</label>
            <div class="xi-editor">
              <TextEditorBox class="branding-tabs" :options="options" :editor="panel.editor" />
              <editor-content class="xi-editor-content" :editor="panel.editor" />
            </div>
          </div>

          <div class="xi-panel__controls">
            <CustomSwitch v-model="panel.active" :label="options.Lang.additional_information_tab.active || 'Active'" />
            <CustomSwitch v-model="panel.is_open_default" :label="options.Lang.additional_information_tab.open || 'Open by default'" />
            <div class="xi-sort">
              <span>{{ options.Lang.additional_information_tab.order || 'Order' }}</span>
              <input type="number" v-model="panel.sort_order" min="1" :max="extraInformationPanels.length" />
            </div>
          </div>
        </div>
      </div>

      <CustomButton class="xi-add" variant="primary" icon="add"
                    :label="options.Lang.additional_information_tab.add || 'Add panel'"
                    @click="addExtraInformationPanel" />
    </div>

    <div v-if="!hideSave" class="buttons-aligned-right">
      <CustomButton variant="primary" :label="options.Lang.Speichern" @click="submitTabContent" />
    </div>

  </div>
</template>
<script>
import TextEditorBox from '../components/basics/TextEditorBox.vue'
import VariableHint from '../components/parts/VariableHint.vue'
import { TEMPLATE_VARIABLES, CHECKOUT_ONLY_VARIABLES } from '../../../common/templateVariables'
import TextEditorWithHeader from '../components/parts/TextEditorWithHeader.vue'
import DeleteConfirmation from './modals/DeleteConfirmation.vue'
import SalutationChoiceModal from './modals/SalutationChoiceModal.vue'
import SegmentedTabs from '../components/parts/SegmentedTabs.vue'
import CustomButton from '../components/parts/CustomButton.vue'
import CustomSwitch from '../components/parts/CustomSwitch.vue'
import CustomInput from '../components/parts/CustomInput.vue'
import CustomSelect from '../components/parts/CustomSelect.vue'
import { EditorContent } from '@tiptap/vue-2'
import { createRichEditor } from '../utils/editorFactory'
export default {
  name: 'AdditionalInformationTab',
  components: {
    VariableHint,
    TextEditorBox,
    EditorContent,
    DeleteConfirmation,
    SalutationChoiceModal,
    TextEditorWithHeader,
    SegmentedTabs,
    CustomButton,
    CustomSwitch,
    CustomInput,
    CustomSelect
  },
  props: {
    address: { type: Object, required: true },
    options: { type: Object, required: true },
    label: { type: String, required: true },
    editors: { type: Object, required: true },
    values: { type: Object, default: () => ({}) },
    // true = the parent (editBrand "Texts & e-mails" tab) owns the Save button
    // and calls submitTabContent() itself; the two internal buttons are hidden.
    hideSave: { type: Boolean, default: false }
  },
  computed: {
    // ---- unsaved-changes tracking ----
    // Everything this tab would save, as one string (see buildTexts + panels).
    stateSignature() {
      const panels = this.extraInformationPanels.map(p => [p.id, p.title, p.sort_order, !!p.active, !!p.is_open_default]);
      // `internal` mirrors the rich editors (setupEditorSync), so reading it here
      // makes editor changes re-evaluate the signature; buildTexts itself reads
      // the editors directly, which is not reactive.
      return JSON.stringify([this.buildTexts(), this.internal, panels, this.panelTick]);
    },
    isDirty() {
      return this.cleanSignature !== null && this.stateSignature !== this.cleanSignature;
    },
    // Pill tabs for the sections under this language (replaces the collapsibles).
    infoSectionTabs() {
      const L = this.options.Lang || {};
      return [
        { value: 'general', label: L.section_general || 'General & Legal', icon: 'gavel' },
        { value: 'emails', label: L.section_emails || 'Emails & Templates', icon: 'mail_outline' },
        { value: 'extra', label: L.section_extra_info || 'Additional Information', icon: 'info_outline' },
      ];
    },
    showSupplierMail() {
      return this.options.ModulSuppliermail;
    },
    // Checkout texts only matter when the Invoice Checkout module is enabled.
    showCheckoutTexts() {
      return !!this.options.ModulInvoiceCheckout;
    },
    groupedExtraPanels() {
      const rows = [];
      for (let i = 0; i < this.extraInformationPanels.length; i += 2) {
        rows.push(this.extraInformationPanels.slice(i, i + 2));
      }
      return rows;
    },
    salutationT() {
      return (this.options.Lang && this.options.Lang.salutation_select) || {};
    },
    salutationValue() {
      return this.address?.salutation?.selected || null;
    },
    salutationOptions() {
      // Default label carries the locale wording of the language being edited
      // (e.g. "Standard (Sehr geehrte/r)" on the German tab).
      const dearNeutral = this.tabLocaleFile()?.salutations?.dear?.neutral || '';
      const defaultLabel = (this.salutationT.default_option || 'Default') + (dearNeutral ? ` (${dearNeutral})` : '');
      const choices = (this.address?.salutation?.choices || []).map(c => ({
        value: c.id,
        label: c.label,
        editable: true,
        removable: true
      }));
      return [{ value: null, label: defaultLabel }].concat(choices);
    },
    variableDescriptionObject(){
      // Variable list from common/templateVariables.js (same list the replacement uses).
      const out = {};
      TEMPLATE_VARIABLES.forEach(v => { out[v.key] = this.options.Lang.variables[v.key] || v.fallback; });
      return out;
    },
  },
  emits: ['update:values', 'dirty-change', 'saved', 'error', 'deleted', 'request-tab-delete'],
  data() {
    return {
      variables_label: "[title] [firstname] [name] [signatur] [userN] [userE] [userT] [userName] [userEmail] [link] [code]",
      // Only filled after checkout (reservation email); blank in the access email.
      reservationOnlyVars: CHECKOUT_ONLY_VARIABLES,
      showTabDeleteConfirm: false,
      showVarsOpen: false,
      showSalutationModal: false,
      confirmSalutationDelete: false,
      salutationDeleteTarget: null,
      salutationEditTarget: null,
      activeInfoSection: 'general',
      internal: {
        subject: '',
        signature: '',
        welcomeText: '',
        welcomeTextBooked: '',
        infoDocs: '',
        travelReqMsg: '',
        template: '',
        signatureSupplier: '',
        reservationEmailBody: '',
        checkout_insurance_confirm: '',
        checkout_payment_confirm: '',
        checkout_name_text: '',
        checkout_agb_confirm: '',
        extra_title: this.address?.extra_title || '',
        extra_content: this.address?.extra_content || '',
        ...this.values
      },
      extraInformationPanels: [],
      isDefault: this.label.toLowerCase() === 'en' || this.label.toLowerCase() === 'default',
      // Unsaved-changes tracking: `cleanSignature` is the serialized state right
      // after load / save; `panelTick` bumps when a panel rich-editor changes
      // (tiptap editors are not reactive, so they are counted, not watched).
      cleanSignature: null,
      panelTick: 0,
    }
  },
  async mounted() {
    this.setupEditorSync();
    await this.getExtraPanelsForbranding();
    this.markClean();
  },
  methods: {
    // Title-case a label string for display only (every word's first letter upper).
    // Does NOT affect saved values — used purely on <label> text.
    titleCase(str) {
      if (!str) return '';
      return String(str).replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1));
    },
    findTranslationString() {
      if (this.label === 'en') return this.options?.Englisch?.passolution?.default ;
      if (this.label === 'fr') return this.options?.French?.passolution?.default ;
      if (this.label === 'el') return this.options?.Greek?.passolution?.default ;
      if (this.label === 'nl') return this.options?.Dutch?.passolution?.default ;
      return this.options?.German?.passolution?.default;
    },
    // Bundled locale file of the language THIS tab edits (not the CRM UI language).
    tabLocaleFile() {
      if (this.label === 'en') return this.options?.Englisch;
      if (this.label === 'fr') return this.options?.French;
      if (this.label === 'el') return this.options?.Greek;
      if (this.label === 'nl') return this.options?.Dutch;
      return this.options?.German;
    },
    // texts.salutation may be absent on languages saved before this feature —
    // create it reactively before writing into it.
    ensureSalutationObject() {
      if (!this.address.salutation || typeof this.address.salutation !== 'object') {
        this.$set(this.address, 'salutation', { selected: null, choices: [] });
      }
      if (!Array.isArray(this.address.salutation.choices)) {
        this.$set(this.address.salutation, 'choices', []);
      }
    },
    onSalutationSelect(val) {
      this.ensureSalutationObject();
      this.address.salutation.selected = val;
    },
    // choice = null → add mode; a choice object → edit mode (prefilled modal).
    openSalutationModal(choice) {
      this.salutationEditTarget = choice;
      this.showSalutationModal = true;
    },
    closeSalutationModal() {
      this.showSalutationModal = false;
      this.salutationEditTarget = null;
    },
    requestSalutationEdit(opt) {
      const choice = (this.address?.salutation?.choices || []).find(c => c.id === opt.value);
      if (choice) this.openSalutationModal(choice);
    },
    saveSalutationChoice(values) {
      this.ensureSalutationObject();
      const target = this.salutationEditTarget;
      if (target && target.id) {
        // Edit in place — keep the id so the selection stays valid.
        const existing = this.address.salutation.choices.find(c => c.id === target.id);
        if (existing) Object.assign(existing, values);
      } else {
        const entry = { id: 'sal_' + crypto.randomUUID(), ...values };
        this.address.salutation.choices.push(entry);
        this.address.salutation.selected = entry.id;
      }
      this.salutationEditTarget = null;
      this.showSalutationModal = false;
    },
    requestSalutationRemove(opt) {
      this.salutationDeleteTarget = opt;
      this.confirmSalutationDelete = true;
    },
    removeSalutationChoice() {
      const target = this.salutationDeleteTarget;
      this.salutationDeleteTarget = null;
      if (!target || !this.address?.salutation) return;
      const sal = this.address.salutation;
      sal.choices = (sal.choices || []).filter(c => c.id !== target.value);
      // Deleting the active choice falls back to Default (locale wording).
      if (sal.selected === target.value) sal.selected = null;
    },
    // Clean shape for persistence; null when nothing custom exists so the
    // texts JSON stays free of empty scaffolding.
    normalizedSalutation() {
      const sal = this.address?.salutation;
      if (!sal || typeof sal !== 'object') return null;
      const choices = (Array.isArray(sal.choices) ? sal.choices : [])
        .filter(c => c && c.id)
        .map(c => ({ id: c.id, label: c.label, male: c.male, female: c.female, neutral: c.neutral }));
      if (!choices.length) return null;
      const selected = choices.some(c => c.id === sal.selected) ? sal.selected : null;
      return { selected, choices };
    },
    async addExtraInformationPanel() {
      const panel = {
        uid: crypto.randomUUID(),
        language_code: this.label,
        branding_id: this.options.brand_id,
        sort_order: this.extraInformationPanels.length+1,
        title: '',
        content_html: '',
        id: null,
        editor: null,
        _confirmDelete: false
      };
      panel.editor = this.trackPanelEditor(createRichEditor(panel.content_html));
      this.extraInformationPanels.push(panel);
    },
    async deleteExtraInformationBox(id){
      await this.doPost({id:id}, 'extra-information/delete', (res) => {
        return res.msg === "success";
      });
    },
    async getExtraPanelsForbranding(){
      const params = {
        branding_id: this.options.brand_id,
        user_id: this.options.user.id,
        language_code: this.label
      }
      await this.doPost({data: params}, 'extra-information/list', (res) => {
        if(res.data && res.data.extraInformation){
          this.extraInformationPanels = res.data.extraInformation.map((panel, i) => ({
            ...panel,
            is_open_default: !!panel.is_open_default,
            active: !!panel.active,
            uid: crypto.randomUUID(),
            editor: this.trackPanelEditor(createRichEditor(panel.content_html || '')),
            _confirmDelete: false
          }));
          this.normalizeSortOrders();
        }
      });
    },
    async removeExtraInformationPanel(idx, box) {
      if(box.id){
        await this.deleteExtraInformationBox(box.id);
      }
      const panel = this.extraInformationPanels[idx];
      if (panel && panel.editor) {
        panel.editor.destroy();
      }
      this.extraInformationPanels.splice(idx, 1);
    },
    async saveExtraInformationPanels() {
      for (const panel of this.extraInformationPanels) {
        const payload = {
          id: panel.id || null,
          branding_id: this.options.brand_id,
          language_code: panel.language_code,
          user_id: this.options.user.id,
          title: panel.title,
          content_html: panel.editor ? panel.editor.getHTML() : panel.content,
          sort_order: panel.sort_order || 0,
          active: panel.active ? 1 : 0,
          is_open_default: panel.is_open_default ? 1 : 0,
        };
        await this.doPost({ data: payload }, 'extra-information/create', async () => {
          await this.getExtraPanelsForbranding()
        });
      }
    },
    normalizeSortOrders() {
      // Ensure unique sequential sort_order (0..n)
      this.extraInformationPanels.sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
      );
    },
    requestConfirmation() {
      this.$emit('request-tab-delete', this);
    },
    confirmDeletion() {
      const data = {
        lang_code: this.label,
        brand_id: this.options.brand_id,
        user_id: this.options.user.id
      };
      this.doPost({data}, 'branding/languages/deleteTab', (res) => {
        this.$emit('deleted');
      });
    },
    setupEditorSync() {
      const map = {
        subject: 'subject',
        signature: 'signature',
        welcomeText: 'welcomeText',
        welcomeTextBooked: 'welcomeTextBooked',
        infoDocs: 'infoDocs',
        travelReqMsg: 'travelReqMsg',
        template: 'template',
        signatureSupplier: 'signatureSupplier',
        reservationEmailBody: 'reservationEmailBody',
        checkout_insurance_confirm: 'checkout_insurance_confirm',
        checkout_payment_confirm: 'checkout_payment_confirm',
        checkout_name_text: 'checkout_name_text',
        checkout_agb_confirm: 'checkout_agb_confirm',
        extra_content: 'extra_content'
      };
      const isHtmlEmpty = html => !html || html.replace(/<[^>]*>/g, '').trim() === ''

      Object.keys(map).forEach(key => {
        const editor = this.editors?.[key]
        if (!editor) return

        // If we already have stored content, load it
        if (this.internal[key]) {
          editor.commands.setContent(this.internal[key])
        } else if (key === 'travelReqMsg') {
          // Only here: inject default if still empty
          if (isHtmlEmpty(editor.getHTML()) && isHtmlEmpty(this.internal.travelReqMsg)) {
            const def = this.findTranslationString() || ''
            if (def) {
              editor.commands.setContent(def)
              this.internal.travelReqMsg = def
            }
          }
        }

        // Sync back on changes
        editor.on('update', () => {
          this.internal[key] = editor.getHTML()
        })
      })
    },
    syncEditorContent() {
      const result = {};
      const fields = ['subject', 'signature', 'welcomeText', 'welcomeTextBooked', 'infoDocs', 'travelReqMsg', 'template', 'signatureSupplier', 'reservationEmailBody', 'checkout_insurance_confirm', 'checkout_payment_confirm', 'checkout_name_text', 'checkout_agb_confirm', 'extra_content'];
      fields.forEach((key) => {
        result[key] = this.editors?.[key]?.getHTML() || '';
      });
      return result;
    },
    // Mark the current state as saved (dirty = differs from this snapshot).
    markClean() {
      this.cleanSignature = this.stateSignature;
    },
    trackPanelEditor(editor) {
      if (editor && typeof editor.on === 'function') {
        editor.on('update', () => { this.panelTick++; });
      }
      return editor;
    },
    // The per-language texts object exactly as saveTab stores it.
    buildTexts() {
      const editorContent = this.syncEditorContent();
      return {
        datenschutz: this.address?.datenschutz || null,
        impressum: this.address?.impressum || null,
        hello: editorContent.welcomeText || null,
        hello_booked: editorContent.welcomeTextBooked || null,
        infoDocs: editorContent.infoDocs || null,
        travel_req_msg: editorContent.travelReqMsg || null,
        signature: editorContent.signature || null,
        casual: this.address?.casual || false,
        travel_regulations: this.address?.travel_regulations || null,
        logoback: this.options?.logoback || null, // if you have it
        reply: this.address?.reply || null,
        replyzugang: this.address?.replyzugang || null,
        favicon: this.options?.favicon || null,   // if you have it
        agbs: this.address?.agbs || null,
        emailCustomer: editorContent.template || null,
        emailBetreffSupplier: this.address?.emailBetreffSupplier || null,
        reservation_email_body: editorContent.reservationEmailBody || null,
        emailBetreffCustomer: editorContent.subject || null,
        vermittelt: this.address?.vermittelt || null,
        reiseversicherung: this.address?.reiseversicherung || null,
        mitarbeiterbezeichnung: this.address?.mitarbeiterbezeichnung || null,
        emailSupplierSig: editorContent.signatureSupplier || null,
        amt: this.address?.amt || null,
        extra_title: this.internal.extra_title || null,
        extra_content: this.internal.extra_content || null,
        // Per-language salutation choices for [title] (null = locale default).
        salutation: this.normalizedSalutation()
        // Checkout texts are no longer edited here — they live on the attached
        // Checkout Profile (Extra Features → Checkout). Not written from branding.
      };
    },
    // Saves this language's texts + extra panels. Resolves true on success so the
    // parent can save several languages in one go.
    async submitTabContent() {
      const texts = this.buildTexts();
      let ok = false;

      const payload = {
        lang_code: this.label === "Default" ? 'de' : this.label,
        brand_id: this.options.brand_id,
        user_id: this.options.user.id,
        texts: JSON.stringify(texts)
      };
      await this.doPost({ data: payload }, 'branding/languages/saveTab', async(res) => {
        if(res.msg === "success"){
          // Add default passolution text only if still empty
          const ed = this.editors?.travelReqMsg
          const isEmpty = html => !html || html.replace(/<[^>]*>/g, '').trim() === ''
          if (ed && isEmpty(this.internal.travelReqMsg) && isEmpty(ed.getHTML())) {
            const def = this.findTranslationString() || ''
            if (def) {
              ed.commands.setContent(def)
              this.internal.travelReqMsg = def
            }
          }
          await this.saveExtraInformationPanels()
          this.markClean()
          ok = true
          this.$emit('saved')
        }else{
          this.$emit('error');
        }
      });
      return ok;
    },
  },
  watch: {
    internal: {
      deep: true,
      handler(newVals) {
        this.$emit('update:values', newVals);
      }
    },
    isDirty(val) {
      this.$emit('dirty-change', val);
    }
  }
}
</script>
<style lang="scss">
@use "../assets/style/AdditionalInformationTab/extraInformation.scss";
// Shared rich-editor box look (.xi-editor / toolbar / .xi-editor-content) —
// also used by the Checkout profile step tabs so they match exactly.
@use "../assets/style/General/richEditorBox.scss";

/* Section pill switcher bar (General / Emails / Additional Information) */
.info-tab-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin: 0.5rem 0 1.5rem;
}
.info-section { animation: info-fade .2s ease; }
@keyframes info-fade {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* "casual" switch row inside General & Legal */
.xi-switch {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 1.25rem;
}
.xi-switch .md-switch { margin: 0 !important; }
.xi-switch__icon { font-size: 18px !important; color: #888 !important; margin-right: 0.1rem; }

/* Salutation select row (Emails section) */
.xi-salutation {
  display: flex;
  align-items: flex-end;
  gap: 0.9rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}
.xi-salutation__field { width: 320px; max-width: 100%; margin-bottom: 0; }
.xi-salutation__hint { font-size: 0.8rem; color: #999; padding-bottom: 0.7rem; }

/* Variables reference accordion (Emails section) */
.xi-vars { margin-bottom: 1.25rem; }
.xi-vars__toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #f1f3f5;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 0.9rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: #444;
  width: 100%;
  text-align: left;
}
.xi-vars__toggle:hover { background: #e9ecef; }
.xi-vars__hint { color: #888; font-style: italic; font-weight: 400; }
.xi-vars__scope { color: #888; font-size: 0.9em; margin-left: 4px; }
.xi-vars__table {
  width: 100%;
  max-width: 760px;
  border-collapse: collapse;
  font-size: 0.82rem;
  margin-top: 0.75rem;
}
.xi-vars__table th {
  text-align: left;
  padding: 6px 8px;
  border-bottom: 2px solid #e3e3e3;
  color: #555;
}
.xi-vars__table td {
  padding: 5px 8px;
  border-bottom: 1px solid #f1f1f1;
  color: #555;
}
.xi-vars__table .xi-vars__key {
  font-family: monospace;
  background: #eef1f4;
  border-radius: 4px;
  padding: 1px 6px;
  color: #2c2c2c;
}

/* ===== Additional Information (extra-info) — clean card layout ===== */
.xi-intro { margin-bottom: 1.5rem; }
.xi-field { display: flex; flex-direction: column; margin-bottom: 1rem; }
.xi-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 0.3rem;
}
.xi-input {
  width: 100%;
  border: 1px solid #dcdcdc !important;
  border-radius: 8px !important;
  padding: 0.55rem 0.75rem !important;
  font-size: 0.9rem;
  background: #fafafa;
  box-sizing: border-box;
}
/* .xi-editor / .xi-editor-content live in the shared richEditorBox partial. */
.xi-explanation { font-size: 0.8rem; color: #999; margin: 0.25rem 0 0; }

/* Section wrapper — bare (no card), so General & Emails match the flat look of
   the Additional Information intro. Only inner pieces (editors, panels) are boxed. */
.xi-card {
  padding: 0.25rem 0 1rem;
  margin-bottom: 0;
}
.xi-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.5rem;
}
/* Short inputs (General & Legal) pack tighter — up to 3 per row. */
.xi-grid--dense {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.xi-grid .xi-field { margin-bottom: 0; }
@media (max-width: 1100px) { .xi-grid--dense { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 700px) {
  .xi-grid,
  .xi-grid--dense { grid-template-columns: 1fr; }
}

.xi-panels {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}
@media (max-width: 900px) { .xi-panels { grid-template-columns: 1fr; } }
.xi-panel {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}
.xi-panel__head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.xi-panel__num {
  width: 1.9rem; height: 1.9rem; flex-shrink: 0;
  border-radius: 50%;
  background: #eef1f4;
  color: #555;
  font-weight: 600;
  display: flex; align-items: center; justify-content: center;
}
.xi-panel__title { flex: 1; }
.xi-panel__remove { margin: 0 !important; flex-shrink: 0; }
.xi-panel__remove .md-icon { color: #d9534f !important; }

.xi-panel__controls {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f1f1;
}
.xi-toggle { display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; font-weight: 600; color: #555; }
.xi-toggle .md-switch { margin: 0 !important; }
.xi-sort { display: flex; align-items: center; gap: 0.4rem; margin-left: auto; font-size: 0.85rem; font-weight: 600; color: #555; }
.xi-sort input {
  width: 56px;
  padding: 0.3rem 0.4rem;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  font-size: 0.85rem;
  box-sizing: border-box;
}

.xi-add { margin-top: 0.5rem; }
.xi-add .md-icon { margin-right: 0.3rem; }
</style>